// The Daily Pop's rules, free of THREE/React so every number here is
// unit-tested (same pattern as playground/juice.ts and worker/tally.ts).
//
// One sky per calendar day, identical for every Popper on Earth: the date is
// hashed into a seed, the seed drives a tiny PRNG, and the PRNG lays out five
// rounds of balloons. Nothing touches the server — the puzzle is computed on
// the device, the result lives in localStorage, and the share text carries it
// anywhere a message can go. No leaderboard on purpose: a board needs a
// crowd, and this hall has to be fun for the first Popper alone.

import { hashString, rng } from "../../lib/seeded";

/** Daily #1 — the day the hall opened. Local calendar dates, like Wordle. */
export const DAILY_EPOCH = "2026-10-14";

export const ROUND_COUNT = 5;
/** A round the Popper can't clear in this long is closed for them. */
export const ROUND_LIMIT_S = 15;
/** Every wrong pop adds this to the day's time. */
export const MISS_PENALTY_S = 1.5;

/** Index into the Atrium palette (playground/palette.ts, tokens.css). */
export type ColorIndex = 0 | 1 | 2 | 3 | 4 | 5;
/** 0 plain · 1 rings · 2 stripes — so the call never rests on colour alone. */
export type Mark = 0 | 1 | 2;

export type PuzzleBalloon = {
  /** Normalised position: x,y in -1..1, z in -1..1 (the field scales it). */
  x: number;
  y: number;
  z: number;
  scale: number;
  color: ColorIndex;
  mark: Mark;
  phase: number;
  target: boolean;
};

export type Round = {
  /** The kind being called: pop every balloon wearing this colour + mark. */
  call: { color: ColorIndex; mark: Mark };
  balloons: PuzzleBalloon[];
  targets: number;
  /** Drift multiplier — the wind picks up in the last round. */
  wind: number;
};

export type Puzzle = { key: string; number: number; rounds: Round[] };

/** Colour sets that stay readable side by side at a glance. Close pairs
 *  (magenta/coral, cyan/mint) never share a round. */
const TRIADS: readonly (readonly ColorIndex[])[] = [
  [0, 1, 2],
  [3, 4, 5],
  [0, 2, 5],
  [1, 3, 4],
];

/** balloons, targets, kinds on the field, wind — round 1 is a handshake. */
const ROUND_SHAPES: readonly { n: number; t: number; kinds: number; wind: number }[] = [
  { n: 7, t: 3, kinds: 2, wind: 1 },
  { n: 9, t: 3, kinds: 3, wind: 1 },
  { n: 11, t: 4, kinds: 3, wind: 1.15 },
  { n: 13, t: 5, kinds: 3, wind: 1.3 },
  { n: 15, t: 5, kinds: 3, wind: 1.7 },
];

// ---------- dates ----------

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** The Popper's own calendar date as YYYY-MM-DD. */
export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Parses YYYY-MM-DD to a UTC day count; null if it isn't a real date. */
function dayIndex(key: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const da = Number(m[3]);
  const ms = Date.UTC(y, mo - 1, da);
  const back = new Date(ms);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== da) {
    return null;
  }
  return Math.round(ms / 86_400_000);
}

/** Daily #N for a date key — counted in whole calendar days, DST-proof. */
export function dailyNumber(key: string): number {
  const a = dayIndex(DAILY_EPOCH)!;
  const b = dayIndex(key);
  return b === null ? 0 : b - a + 1;
}

/** The date key one calendar day before `key`. */
export function previousKey(key: string): string {
  const i = dayIndex(key);
  if (i === null) return "";
  const d = new Date((i - 1) * 86_400_000);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** Milliseconds until the Popper's next local midnight — the next sky. */
export function msUntilNextSky(now: Date): number {
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return Math.max(0, next.getTime() - now.getTime());
}

/** 3h 04m 09s → "03:04:09". */
export function formatCountdown(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${pad2(Math.floor(s / 3600))}:${pad2(Math.floor((s % 3600) / 60))}:${pad2(s % 60)}`;
}

/** True when `key` is a real date at or after Daily #1 — `?day=` honours only these. */
export function isPlayableKey(key: string): boolean {
  return dayIndex(key) !== null && dailyNumber(key) >= 1;
}

// ---------- the seeded sky ----------

function shuffle<T>(list: T[], r: () => number): T[] {
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
}

/**
 * Spreads `n` points over the normalised field with a minimum spacing, so no
 * two balloons start on top of each other. Rejection sampling with a fixed
 * attempt budget — deterministic because the stream is.
 */
function scatter(n: number, r: () => number): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];
  let minD = 0.5;
  let attempts = 0;
  while (pts.length < n) {
    const p = { x: r() * 1.8 - 0.9, y: r() * 1.6 - 0.8 };
    if (pts.every((q) => (q.x - p.x) ** 2 + (q.y - p.y) ** 2 >= minD * minD)) pts.push(p);
    if (++attempts % 200 === 0) minD *= 0.85; // crowded: loosen, never loop forever
  }
  return pts;
}

/** The whole day's puzzle. Same key in, same sky out — on every device. */
export function buildPuzzle(key: string): Puzzle {
  const r = rng(hashString(`poparium-daily:${key}`));
  const rounds: Round[] = ROUND_SHAPES.map((shape) => {
    const triad = TRIADS[Math.floor(r() * TRIADS.length)];
    const colors = shuffle([...triad], r).slice(0, shape.kinds);
    const marks = shuffle<Mark>([0, 1, 2], r);
    const kinds = colors.map((color, i) => ({ color, mark: marks[i] }));
    const call = kinds[0];

    // Targets first, then decoys spread evenly across the remaining kinds.
    const plan: { color: ColorIndex; mark: Mark; target: boolean }[] = [];
    for (let i = 0; i < shape.t; i++) plan.push({ ...call, target: true });
    for (let i = 0; i < shape.n - shape.t; i++) {
      plan.push({ ...kinds[1 + (i % (kinds.length - 1))], target: false });
    }
    shuffle(plan, r);

    const pts = scatter(shape.n, r);
    const balloons = plan.map((p, i) => ({
      x: pts[i].x,
      y: pts[i].y,
      z: r() * 1.2 - 0.6,
      scale: 0.7 + r() * 0.45,
      color: p.color,
      mark: p.mark,
      phase: r() * Math.PI * 2,
      target: p.target,
    }));
    return { call, balloons, targets: shape.t, wind: shape.wind };
  });
  return { key, number: dailyNumber(key), rounds };
}

// ---------- scoring ----------

/** 🟩 clean & quick · 🟨 close · 🟥 messy · ⬛ ran out of sky. */
export type Grade = "green" | "yellow" | "red" | "timeout";

export type RoundResult = { time: number; misses: number; timedOut: boolean };

/** A round's par: a beat to read the call, plus a beat per target. */
export function parFor(targets: number): number {
  return 1.2 + 0.6 * targets;
}

export function gradeRound(res: RoundResult, targets: number): Grade {
  if (res.timedOut) return "timeout";
  const par = parFor(targets);
  if (res.misses === 0 && res.time <= par) return "green";
  if (res.misses <= 1 && res.time <= par * 1.8) return "yellow";
  return "red";
}

const GRADE_EMOJI: Record<Grade, string> = {
  green: "🟩",
  yellow: "🟨",
  red: "🟥",
  timeout: "⬛",
};

export function gradeGrid(grades: Grade[]): string {
  return grades.map((g) => GRADE_EMOJI[g]).join("");
}

/** The day's time: every round's clock plus the miss penalties. Lower wins. */
export function totalTime(results: RoundResult[]): number {
  return results.reduce((s, r) => s + Math.min(r.time, ROUND_LIMIT_S) + r.misses * MISS_PENALTY_S, 0);
}

/** 12.345 → "12.3s" */
export function formatTime(s: number): string {
  return `${(Math.round(s * 10) / 10).toFixed(1)}s`;
}

/** The text a Popper pastes into a chat. Short enough for any feed. */
export function shareText(number: number, total: number, grid: string, origin: string): string {
  return `Poparium Daily #${number} · ${formatTime(total)}\n${grid}\n${origin}/lab/daily`;
}

// ---------- the record (localStorage) ----------

export type DayEntry = { total: number; grid: string };

export type DailyRecord = {
  v: 1;
  /** Last date key with an official finish. */
  last: string;
  streak: number;
  best: number | null;
  played: number;
  days: Record<string, DayEntry>;
};

/** localStorage key for the record — read by the hall and the hero teaser. */
export const DAILY_RECORD_KEY = "poparium:daily";

export const EMPTY_RECORD: DailyRecord = { v: 1, last: "", streak: 0, best: null, played: 0, days: {} };

/** Keep the ledger small: the most recent days only. */
const KEEP_DAYS = 60;

/**
 * Folds one official finish into the record. Finishing twice on the same day
 * changes nothing — the first run is the one that counts.
 */
export function recordFinish(prev: DailyRecord, key: string, entry: DayEntry): DailyRecord {
  if (prev.days[key]) return prev;
  const streak = prev.last === previousKey(key) ? prev.streak + 1 : 1;
  const days = { ...prev.days, [key]: entry };
  const keys = Object.keys(days).sort();
  for (const k of keys.slice(0, Math.max(0, keys.length - KEEP_DAYS))) delete days[k];
  return {
    v: 1,
    last: key,
    streak,
    best: prev.best === null ? entry.total : Math.min(prev.best, entry.total),
    played: prev.played + 1,
    days,
  };
}

/** The streak as it stands today: broken if yesterday went unplayed. */
export function liveStreak(rec: DailyRecord, todayKey: string): number {
  return rec.last === todayKey || rec.last === previousKey(todayKey) ? rec.streak : 0;
}

/** Tolerant parse: anything malformed reads as a fresh record. */
export function parseRecord(raw: string | null): DailyRecord {
  if (!raw) return EMPTY_RECORD;
  try {
    const o = JSON.parse(raw) as Partial<DailyRecord>;
    if (o.v !== 1 || typeof o.days !== "object" || o.days === null) return EMPTY_RECORD;
    return {
      v: 1,
      last: typeof o.last === "string" ? o.last : "",
      streak: typeof o.streak === "number" ? o.streak : 0,
      best: typeof o.best === "number" ? o.best : null,
      played: typeof o.played === "number" ? o.played : 0,
      days: o.days as Record<string, DayEntry>,
    };
  } catch {
    return EMPTY_RECORD;
  }
}
